import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  type Bias,
  biasTone,
  PROTECTION_META,
  type ProtectedState,
  ROLE_META,
  type PositionRole,
  SYSTEM_STATE_META,
  type SystemState,
  type Tone,
  toneBadge,
  toneDot,
} from "@/lib/semantics";

interface ToneBadgeProps {
  tone?: Tone;
  children: ReactNode;
  /** Render a leading status dot in the tone colour. */
  dot?: boolean;
  className?: string;
  title?: string;
  testId?: string;
}

/** Generic SPEC §14 tone-coloured pill. Base for the domain badges below. */
export function ToneBadge({
  tone = "neutral",
  children,
  dot = false,
  className,
  title,
  testId,
}: ToneBadgeProps) {
  return (
    <span
      data-slot="badge"
      data-testid={testId}
      title={title}
      className={cn(
        "inline-flex w-fit items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap shadow-[inset_0_1px_0_0_color-mix(in_oklab,#ffffff_8%,transparent)] backdrop-blur-sm",
        toneBadge(tone),
        className,
      )}
    >
      {dot && <span className={cn("size-1.5 rounded-full", toneDot(tone))} />}
      {children}
    </span>
  );
}

export function BiasBadge({
  bias,
  className,
}: {
  bias: Bias;
  className?: string;
}) {
  return (
    <ToneBadge tone={biasTone(bias)} dot className={className}>
      {bias}
    </ToneBadge>
  );
}

export function RoleBadge({
  role,
  className,
}: {
  role: PositionRole;
  className?: string;
}) {
  const meta = ROLE_META[role];
  return (
    <ToneBadge tone={meta.tone} title={meta.help} className={className}>
      {meta.label}
    </ToneBadge>
  );
}

export function ProtectionBadge({
  state,
  className,
}: {
  state: ProtectedState;
  className?: string;
}) {
  const meta = PROTECTION_META[state];
  return (
    <ToneBadge tone={meta.tone} dot title={meta.help} className={className}>
      {meta.short}
    </ToneBadge>
  );
}

export function SystemStateBadge({
  state,
  className,
}: {
  state: SystemState;
  className?: string;
}) {
  const meta = SYSTEM_STATE_META[state];
  return (
    <ToneBadge tone={meta.tone} dot className={className}>
      {meta.label}
    </ToneBadge>
  );
}

export function ModeBadge({
  mode,
  className,
}: {
  mode: string;
  className?: string;
}) {
  const isLive = mode === "LIVE";
  return (
    <ToneBadge
      tone={isLive ? "live" : "info"}
      className={cn("font-semibold tracking-wide", className)}
    >
      {mode}
    </ToneBadge>
  );
}
